import { Airport } from "@/models/airport";
import { getDistance } from "@/utils/airport_distance";
import { dt, language, locale } from "./strings";
import { DailyStrings } from "./translations";
import { ClueType, DailyGuess, DailyPuzzle, GuessBand } from "./types";

export const MAX_GUESSES = 5;
// A wrong guess within this many miles of the destination counts as "near".
// The threshold stays in miles for every player, whatever unit is displayed.
export const NEAR_MILES = 500;

const CLUE_LABEL_KEYS: Record<ClueType, keyof DailyStrings> = {
  flight_time: "clueFlightTime",
  time_left: "clueTimeLeft",
  on_arrival: "clueOnArrival",
  airline: "clueAirline",
  local_knowledge: "clueLocalKnowledge",
  possible_airports: "clueAirports",
};

// "On arrival", in the player's language.
export const clueLabel = (type: ClueType): string => dt(CLUE_LABEL_KEYS[type]);

// "Clue 2 · On arrival"
export const clueHeading = (number: number, type: ClueType): string =>
  `${dt("clueNumber", { number })} · ${clueLabel(type)}`;

export const evaluateGuess = (
  puzzle: DailyPuzzle,
  airport: Airport
): DailyGuess => {
  const isCorrect = airport.iata === puzzle.destination.iata;
  const distanceMiles = isCorrect ? 0 : getDistance(airport, puzzle.destination);
  const band: GuessBand = isCorrect
    ? "correct"
    : distanceMiles <= NEAR_MILES
    ? "near"
    : "far";
  return { airport, distanceMiles, band };
};

export const hasWon = (guesses: DailyGuess[]): boolean =>
  guesses.some((guess) => guess.band === "correct");

export const isFinished = (guesses: DailyGuess[]): boolean =>
  hasWon(guesses) || guesses.length >= MAX_GUESSES;

// The one place distances are formatted: "680 miles away", in the player's
// language. The game works in miles; converting to km for players whose
// locale uses it belongs here (not built in v1).
export const formatDistanceAway = (miles: number): string => {
  if (miles < 10) return dt("awayUnder10");
  const rounded = miles >= 100 ? Math.round(miles / 10) * 10 : Math.round(miles / 5) * 5;
  return milesAway(rounded);
};

// "680 miles away" for a number already rounded, as the count climbs.
export const milesAway = (rounded: number): string =>
  dt("away", { distance: rounded.toLocaleString(locale()) });

export const formatDuration = (minutes: number): string =>
  `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ordinal = (day: number): string => {
  if (day % 100 >= 11 && day % 100 <= 13) return `${day}th`;
  const suffix = ["th", "st", "nd", "rd"][day % 10] ?? "th";
  return `${day}${suffix}`;
};

// "2026-10-12" -> "Oct 12th", or the short date of another language
// ("12. Okt.", "12 oct").
export const formatPuzzleDate = (isoDate: string, inLanguage = language()): string => {
  const [year, month, day] = isoDate.split("-").map(Number);
  if (inLanguage === "en") return `${MONTHS[month - 1]} ${ordinal(day)}`;
  return new Intl.DateTimeFormat(inLanguage, { month: "short", day: "numeric" }).format(
    new Date(year, month - 1, day)
  );
};

export const scoreLabel = (guesses: DailyGuess[]): string =>
  `${hasWon(guesses) ? guesses.length : "X"}/${MAX_GUESSES}`;

const SHARE_SYMBOL: Record<GuessBand, string> = {
  far: "⬜",
  near: "🟨",
  correct: "✅",
};

const SHARE_LINK = "https://flightguesser.com/daily";

// Says where the flight left from, never where it went:
//   Oct 12th  3/5
//   LHR → ⬜⬜✅
//   https://flightguesser.com/daily
export const buildShareText = (
  puzzle: DailyPuzzle,
  guesses: DailyGuess[]
): string =>
  `${formatPuzzleDate(puzzle.date)}  ${scoreLabel(guesses)}\n` +
  `${puzzle.origin.iata} → ${guesses.map((guess) => SHARE_SYMBOL[guess.band]).join("")}\n` +
  SHARE_LINK;

// Players get the puzzle for the date where they are: today's date on their
// own clock, as YYYY-MM-DD.
export const localDateIso = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

// The next puzzle arrives at the player's own midnight.
export const msUntilNextPuzzle = (): number => {
  const now = new Date();
  const nextMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1
  );
  return nextMidnight.getTime() - now.getTime();
};

export const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const parts = [
    Math.floor(totalSeconds / 3600),
    Math.floor((totalSeconds % 3600) / 60),
    totalSeconds % 60,
  ];
  return parts.map((part) => String(part).padStart(2, "0")).join(":");
};

// Progress and the "has seen how to play" flag live in localStorage, next to
// the classic game's session id.
const HELP_SEEN_KEY = "fg_daily_help_seen";
const guessesKey = (isoDate: string) => `fg_daily_guesses_${isoDate}`;

export const hasSeenHelp = (): boolean =>
  localStorage.getItem(HELP_SEEN_KEY) === "1";

export const markHelpSeen = (): void =>
  localStorage.setItem(HELP_SEEN_KEY, "1");

// Where a puzzle's progress is kept. A previewed draft has its own slot, so
// trying one out never touches the real day's game.
export const progressKey = (puzzle: DailyPuzzle): string =>
  puzzle.draft ? `draft-${puzzle.draft}` : puzzle.date;

export const loadGuessedIatas = (isoDate: string): string[] => {
  try {
    const stored = JSON.parse(localStorage.getItem(guessesKey(isoDate)) ?? "[]");
    return Array.isArray(stored) ? stored.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
};

export const saveGuessedIatas = (isoDate: string, iatas: string[]): void =>
  localStorage.setItem(guessesKey(isoDate), JSON.stringify(iatas));

// Testing aid: /daily?reset clears today's guesses and shows how-to-play again.
export const resetDailyProgress = (isoDate: string): void => {
  localStorage.removeItem(guessesKey(isoDate));
  localStorage.removeItem(HELP_SEEN_KEY);
};
